import type { ZonedDateTimeString } from "@sa-nu/shared-utils/date";
import {
  getCurrentTime,
  PlainDate,
  ZonedDateTime,
} from "@sa-nu/shared-utils/date";

import type { FragmentType } from "../../../../__generated__/gql";
import { graphql } from "../../../../__generated__/gql";
import { useFragment } from "../../../../__generated__/gql";
import { AccommodationFacilityType } from "../../../../__generated__/gql/graphql";
import { getAccommodationName } from "../../../../utils";
import type { AccommodationAndPeriodMessages } from "../accommodation-and-period/accommodation-and-period";
import { AccommodationAndPeriod } from "../accommodation-and-period/accommodation-and-period";

export type AccommodationReservationInfoMessages =
  AccommodationAndPeriodMessages;

// getAccommodationName() と同じロジックで非表示の期間を判定
export function isNowEarlierThanTwoDaysBeforeCheckIn(
  reservedFrom: ZonedDateTimeString,
) {
  const now = getCurrentTime();
  const twoDaysBeforeCheckIn = ZonedDateTime.from(reservedFrom).subtract({
    days: 2,
  });
  return ZonedDateTime.compare(now, twoDaysBeforeCheckIn) < 0;
}

const Fragment = graphql(`
  fragment AccommodationReservationInfo on AccommodationReservation {
    id
    provider {
      __typename
    }
    firstDate
    lastDate
    reservedFrom
    accommodation {
      name
      code
      site {
        name
        slug
        area {
          slug
        }
      }
      architectureType {
        name
        series
      }
      facilityTypes
    }
    detail {
      bringDog
      useEvCharger
    }
    stayTicketUnit {
      reservationInfo {
        name
      }
    }
  }
`);

export function AccommodationReservationInfo({
  reservation,
  className,
  messages,
}: {
  reservation: FragmentType<typeof Fragment>;
  className?: string;
  messages?: AccommodationReservationInfoMessages;
}) {
  const data = useFragment(Fragment, reservation);

  const accommodationName = getAccommodationName({
    provider: data.provider,
    reservedFrom: data.reservedFrom,
    accommodation: data.accommodation,
  });
  const accommodationCode =
    data.provider.__typename ===
      "OnlineTravelAgentAccommodationReservationProvider" &&
    isNowEarlierThanTwoDaysBeforeCheckIn(data.reservedFrom)
      ? undefined
      : data.accommodation.code;
  const architectureName = data.accommodation.architectureType.name;
  const checkInDate = PlainDate.from(data.firstDate);
  const checkOutDate = PlainDate.from(data.lastDate);
  const ticketMasterName = data.stayTicketUnit?.reservationInfo?.name;
  const facilityTypes = data.accommodation.facilityTypes.filter((type) => {
    if (type === AccommodationFacilityType.DogFriendly) {
      return data.detail.bringDog;
    } else if (type === AccommodationFacilityType.EvCharger) {
      return data.detail.useEvCharger;
    } else {
      return true;
    }
  });

  return (
    <AccommodationAndPeriod
      accommodationName={accommodationName}
      accommodationCode={accommodationCode}
      architectureName={architectureName}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      className={className}
      facilityTypes={facilityTypes}
      ticketMasterName={ticketMasterName}
      messages={messages}
    />
  );
}
